// src/pages/PatientAssistantPage.tsx

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MobileContainer } from '../styles/GlobalStyles';
import { getSurgeryByName, getPatientByName, getDoctorById, addQuestion, updateQuestion, getQuestionsByPatientAndDoctor, getPatientSessions, checkFirebaseConnection, createSessionLog, addActionToSession, endSessionLog } from '../services/firebase';
import { OpenAIRealtimeService } from '../services/openai';
import { Surgery, Patient, Doctor, Question, PatientSession, Section } from '../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { useTranslation } from '../i18n';
import { MicIcon, StopIcon, ReconnectIcon, StethoscopeIcon } from '../components/Icons';

// QA 세트 인터페이스
interface QASet {
  question: string;
  answer: string;
  timestamp: Date;
}

// 🔥 로그 관련 유틸리티 함수들
const useActionLogger = (
  patientName: string | undefined,
  institutionNumber: string,
  surgeryName: string | undefined,
  doctorId?: string
) => {
  const sessionId = useRef<string>(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const sessionLogDocId = useRef<string | null>(null);
  const isSessionInitialized = useRef<boolean>(false);
  
  // 세션 초기화 (페이지 로드 시)
  const initializeSession = useCallback(async () => {
    if (!patientName || !surgeryName || isSessionInitialized.current) return;
    
    try {
      console.log('🚀 세션 로그 초기화 시작:', sessionId.current);
      
      const docId = await createSessionLog(
        patientName,
        institutionNumber,
        surgeryName,
        sessionId.current,
        doctorId
      );
      
      sessionLogDocId.current = docId;
      isSessionInitialized.current = true;
      
      // 페이지 진입 로그 추가
      await addActionToSession(
        sessionId.current,
        'page_enter',
        'patient_assistant_page',
        {
          surgeryName,
          patientName,
          doctorName: doctorId || null,
          hasDoctor: !!doctorId
        }
      );
      
      console.log('✅ 세션 로그 초기화 완료:', { sessionId: sessionId.current, docId });
    } catch (error) {
      console.error('❌ 세션 로그 초기화 실패:', error);
    }
  }, [patientName, institutionNumber, surgeryName, doctorId]);
  
  // 행동 로그 추가
  const logAction = useCallback(async (
    actionType: 'click' | 'scroll' | 'voice_start' | 'voice_stop' | 'section_change' | 'page_enter' | 'page_exit',
    actionTarget?: string,
    actionData?: any
  ) => {
    if (!isSessionInitialized.current) {
      console.log('⚠️ 세션이 초기화되지 않음, 로그 건너뜀');
      return;
    }
    
    try {
      // 🔥 undefined 값 필터링
      const cleanActionTarget = actionTarget?.trim() || undefined;
      const cleanActionData = actionData ? 
        Object.fromEntries(
          Object.entries(actionData).filter(([_, value]) => 
            value !== undefined && value !== null && value !== ''
          )
        ) : undefined;
      
      await addActionToSession(
        sessionId.current, 
        actionType, 
        cleanActionTarget, 
        cleanActionData
      );
      console.log('📝 행동 로그 기록:', { actionType, actionTarget: cleanActionTarget, actionData: cleanActionData });
    } catch (error) {
      console.error('❌ 행동 로그 기록 실패:', error);
    }
  }, []);
  
  // 세션 종료
  const endSession = useCallback(async () => {
    if (!isSessionInitialized.current) return;
    
    try {
      await endSessionLog(sessionId.current);
      console.log('🏁 세션 종료 완료:', sessionId.current);
    } catch (error) {
      console.error('❌ 세션 종료 실패:', error);
    }
  }, []);
  
  return { 
    initializeSession, 
    logAction, 
    endSession, 
    sessionId: sessionId.current,
    isInitialized: isSessionInitialized.current
  };
};

// 페이지 컴포넌트
const PatientAssistantPage: React.FC = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { surgeryName, patientName: urlPatientName, doctorId } = useParams<{ surgeryName: string; patientName: string; doctorId?: string; }>();

  // 환자 단계(stage) — pre_surgery (default) | post_surgery | pre_visit
  const stage: 'pre_surgery' | 'post_surgery' | 'pre_visit' = (() => {
    const s = new URLSearchParams(window.location.search).get('stage');
    return s === 'post_surgery' || s === 'pre_visit' ? s : 'pre_surgery';
  })();

  // 기관번호 가져오기
  const [institutionNumber, setInstitutionNumber] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlInstitution = urlParams.get('institution');
    const sessionInstitution = sessionStorage.getItem('institutionNumber');
    return urlInstitution || sessionInstitution || '';
  });

  const [surgery, setSurgery] = useState<Surgery | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [patientSession, setPatientSession] = useState<PatientSession | null>(null);

  // 맞춤형 섹션 사용
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  // QA 관리
  const [qaSets, setQASets] = useState<QASet[]>([]);
  const [, setCurrentQASet] = useState<QASet | null>(null);

  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questionRecord, setQuestionRecord] = useState<Question | null>(null);
  const [aiScript, setAiScript] = useState('');
  const [isRealtimeSpeaking, setIsRealtimeSpeaking] = useState(false);
  const [, setHasAutoReconnectAttempted] = useState(false);

  // 상태 관리
  const [isRecording, setIsRecording] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [, setPendingMessageId] = useState<string | null>(null);
  const [, setIsConnected] = useState(false);
  const [isInitialAIResponseComplete, setIsInitialAIResponseComplete] = useState(false);

  // 음성창 토글 상태
  const [isVoiceAreaOpen, setIsVoiceAreaOpen] = useState(false);

  // 섹션 요약 상태
  const [isSummarizingSection, setIsSummarizingSection] = useState(false);
  const [summarizingSectionIndex, setSummarizingSectionIndex] = useState<number | null>(null);
  const [isSectionSummaryComplete, setIsSectionSummaryComplete] = useState(false);

  // 사용자 질문 관련 상태
  const [hasUserAskedQuestion, setHasUserAskedQuestion] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState<string>('');
  const [, setIsAnswerComplete] = useState(false);

  // refs
  const aiServiceRef = useRef<OpenAIRealtimeService | null>(null);
  const ttsRef = useRef<SpeechSynthesisUtterance | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scriptRef = useRef<HTMLDivElement>(null);
  const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initializingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const hasSavedRef = useRef(false);
  const contentAreaRef = useRef<HTMLDivElement>(null);

  // 클로저 문제 해결을 위한 ref
  const hasUserAskedQuestionRef = useRef(false);
  const pendingQuestionRef = useRef<string>('');
  const surgeryRef = useRef<Surgery | null>(null);
  const patientRef = useRef<Patient | null>(null);
  const doctorRef = useRef<Doctor | null>(null);
  const isSummarizingSectionRef = useRef(false);

  // Firebase 저장 상태
  const [isSavingToFirebase, setIsSavingToFirebase] = useState(false);

  // Chrome 자동재생 정책: AudioContext는 사용자 제스처 후에만 생성 가능.
  // 첫 진입 시 음성 시작 오버레이를 노출하고 사용자가 클릭해야 AI 초기화 시작.
  const [voiceConsented, setVoiceConsented] = useState(false);
  const pendingInitArgsRef = useRef<{ sData: Surgery; pData: Patient; dData?: Doctor; sessionData?: PatientSession } | null>(null);

  // 🔥 행동 로깅 훅 사용
  const { initializeSession, logAction, endSession, isInitialized } = useActionLogger(
    patient?.name,
    institutionNumber,
    surgery?.name,
    doctor?.id
  );

  const stopTTS = useCallback(() => {
    if (speechSynthesis.speaking) speechSynthesis.cancel();
    ttsRef.current = null;
  }, []);

  // Firebase 저장 함수 - 🔥 수정: patientName 사용
  const saveQASetToFirebaseWithData = useCallback(async (
    question: string,
    answer: string,
    surgeryData: Surgery,
    patientData: Patient,
    doctorData?: Doctor
  ) => {
    console.log('🚀 Firebase 저장 함수 호출됨');
    console.log('📋 저장 파라미터 체크:', {
      question: question ? `"${question.substring(0, 50)}..."` : 'EMPTY',
      answer: answer ? `"${answer.substring(0, 50)}..."` : 'EMPTY',
      surgeryData: surgeryData ? surgeryData.name : 'NULL',
      patientData: patientData ? `${patientData.name}` : 'NULL',
      doctorData: doctorData ? doctorData.name : 'NULL',
      institutionNumber: institutionNumber || 'EMPTY'
    });

    if (!surgeryData || !patientData || !question.trim() || !answer.trim() || !institutionNumber) {
      console.error('❌ 필수 데이터 누락');
      return;
    }

    if (isSavingToFirebase) {
      console.log('⚠️ 이미 저장 중, 중복 저장 방지');
      return;
    }

    console.log('✅ 모든 조건 통과, 저장 시작');
    setIsSavingToFirebase(true);

    try {
      const trimmedQuestion = question.trim();
      const trimmedAnswer = answer.trim();

      const isDuplicate = qaSets.some(set =>
        set.question.trim() === trimmedQuestion &&
        set.answer.trim() === trimmedAnswer
      );

      if (isDuplicate) {
        console.log('⚠️ 중복된 QA 세트 감지, 저장하지 않음');
        return;
      }

      console.log('🔍 Firebase 연결 상태 확인');
      const isConnected = await checkFirebaseConnection();
      if (!isConnected) {
        console.error('❌ Firebase 연결 실패');
        alert('Firebase 연결에 실패했습니다. 네트워크를 확인해주세요.');
        return;
      }
      console.log('✅ Firebase 연결 확인됨');

      let currentRecord = questionRecord;

      if (!currentRecord) {
        console.log('📝 로컬에 레코드 없음, DB에서 조회');
        try {
          currentRecord = await getQuestionsByPatientAndDoctor(
            surgeryData.name,
            institutionNumber,
            patientData.name, // 🔥 수정: patientNumber → name
            doctorData?.id
          );

          if (currentRecord) {
            console.log('✅ DB에서 기존 레코드 발견:', {
              id: currentRecord.id,
              existingQuestions: currentRecord.questions.length,
              existingAnswers: currentRecord.answers.length,
              doctorId: currentRecord.doctorId || 'general'
            });
            setQuestionRecord(currentRecord);
          } else {
            console.log('📝 DB에도 레코드 없음, 새로 생성 예정');
          }
        } catch (queryError) {
          console.error('❌ 기존 레코드 조회 오류:', queryError);
        }
      }

      const existingQuestions = currentRecord?.questions || [];
      const existingAnswers = currentRecord?.answers || [];
      const allQuestions = [...existingQuestions, trimmedQuestion];
      const allAnswers = [...existingAnswers, trimmedAnswer];

      if (currentRecord && currentRecord.id) {
        console.log('🔄 기존 레코드 업데이트 시작:', currentRecord.id);

        try {
          await updateQuestion(currentRecord.id, {
            questions: allQuestions,
            answers: allAnswers
          });

          console.log('✅ 기존 레코드 업데이트 성공');

          const updatedRecord = {
            ...currentRecord,
            questions: allQuestions,
            answers: allAnswers
          };
          setQuestionRecord(updatedRecord);

        } catch (updateError) {
          console.error('❌ 레코드 업데이트 실패:', updateError);
          throw updateError;
        }

      } else {
        console.log('📝 새 레코드 생성 시작');

        const questionData = {
          surgeryName: surgeryData.name,
          patientName: patientData.name, // 🔥 수정: patientNumber → patientName
          institutionNumber: institutionNumber,
          ...(doctorData?.id && { doctorId: doctorData.id }),
          questions: allQuestions,
          answers: allAnswers,
          createdAt: new Date()
        };

        try {
          const newId = await addQuestion(questionData);
          console.log('✅ 새 레코드 생성 성공, ID:', newId);

          const newRecord = {
            id: newId,
            ...questionData
          };

          setQuestionRecord(newRecord);

        } catch (addError) {
          console.error('❌ 새 레코드 생성 실패:', addError);
          throw addError;
        }
      }

      const newQASet: QASet = {
        question: trimmedQuestion,
        answer: trimmedAnswer,
        timestamp: new Date()
      };

      const currentQASets = currentRecord ?
        currentRecord.questions.map((q, index) => ({
          question: q,
          answer: currentRecord!.answers[index] || '',
          timestamp: currentRecord!.createdAt
        })) : qaSets;

      const allQASets = [...currentQASets, newQASet];
      setQASets(allQASets);

      console.log('✅ Firebase 저장 완전 완료:', {
        totalQuestions: allQuestions.length,
        totalAnswers: allAnswers.length,
        localQASetsCount: allQASets.length
      });

      // 🔥 로그 기록: QA 저장 완료
      await logAction('voice_stop', 'qa_saved', {
        questionLength: trimmedQuestion.length,
        answerLength: trimmedAnswer.length,
        totalQACount: allQASets.length
      });

      setTimeout(() => {
        setHasUserAskedQuestion(false);
        setPendingQuestion('');
        setIsAnswerComplete(false);
        hasUserAskedQuestionRef.current = false;
        pendingQuestionRef.current = '';

        console.log('🔄 저장 후 상태 리셋 완료');
      }, 300);

    } catch (error) {
      console.error('❌ Firebase 저장 전체 오류:', error);
      alert(`질문/답변 저장 중 오류가 발생했습니다:\n${(error as Error).message}`);
    } finally {
      setIsSavingToFirebase(false);
      console.log('🏁 Firebase 저장 프로세스 종료');
    }
  }, [qaSets, questionRecord, isSavingToFirebase, institutionNumber, logAction]);

  // AI 응답 즉시 중단 함수
  const interruptAIResponse = useCallback(async () => {
    if (!aiServiceRef.current) return;

    try {
      console.log('🛑 AI 응답 즉시 중단 시작');

      if (isRealtimeSpeaking || isWaiting || isSummarizingSection) {
        await aiServiceRef.current.interruptCurrentResponse();

        setIsRealtimeSpeaking(false);
        setIsWaiting(false);
        setIsSummarizingSection(false);
        setSummarizingSectionIndex(null);
        setIsAnswerComplete(false);
        setIsSectionSummaryComplete(false);

        isSummarizingSectionRef.current = false;

        setAiScript('');
        console.log('✅ AI 응답 중단 완료');

        // 🔥 로그 기록: AI 응답 중단
        await logAction('click', 'ai_interrupt');
      }
    } catch (error) {
      console.error('AI 응답 중단 오류:', error);
    }
  }, [isRealtimeSpeaking, isWaiting, isSummarizingSection, logAction]);

  // 🔥 수정된 섹션 변경 핸들러 - 로그 기록 추가
  const handleSectionChange = useCallback(async (idx: number) => {
    if (!sections || !sections[idx]) {
      return;
    }

    const selectedSection = sections[idx];

    // 즉시 UI 상태 업데이트 (응답 시각 피드백 최우선)
    setCurrentSectionIndex(idx);
    setIsSectionSummaryComplete(false);
    setIsSummarizingSection(true);
    setSummarizingSectionIndex(idx);
    setIsWaiting(true);
    setAiScript('');
    isSummarizingSectionRef.current = true;
    hasSavedRef.current = false;

    // 콘텐츠 영역 스크롤 (즉시)
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // 로그는 fire-and-forget (UI 흐름을 막지 않음)
    void logAction('section_change', selectedSection.title, {
      sectionIndex: idx,
      sectionId: selectedSection.id,
      fromSectionIndex: currentSectionIndex
    });

    // 현재 AI 응답 중단 (오디오 큐 비우고 stale chunk 차단)
    await interruptAIResponse();

    // AI 서비스가 연결되어 있으면 맞춤형 섹션 요약 시작
    if (aiServiceRef.current &&
        aiServiceRef.current.isConnected() &&
        connectionStatus === 'connected' &&
        patient) {

      try {
        console.log('📋 맞춤형 섹션 요약 시작:', selectedSection.title);

        // 맞춤형 섹션 요약 요청
        await aiServiceRef.current.requestCustomizedSectionSummary(
          selectedSection,
          patient,
          language,
          patientSession?.specialConditions
        );

        console.log('📋 맞춤형 섹션 요약 요청 전송됨');

        // 로그는 fire-and-forget
        void logAction('voice_start', 'section_summary', {
          sectionIndex: idx,
          sectionTitle: selectedSection.title
        });

      } catch (error) {
        console.error('맞춤형 섹션 요약 요청 오류:', error);
        setIsSummarizingSection(false);
        setSummarizingSectionIndex(null);
        setIsSectionSummaryComplete(false);
        setIsWaiting(false);
        setAiScript('');

        isSummarizingSectionRef.current = false;
      }
    }
  }, [sections, patient, patientSession, connectionStatus, interruptAIResponse, currentSectionIndex, logAction]);

  // 🔥 수정된 음성 녹음 토글 - 로그 기록 추가
  const toggleRecording = useCallback(async () => {
    if (!aiServiceRef.current) return;

    try {
      if (!isRecording) {
        // 🔥 로그 기록: 음성 녹음 시작
        await logAction('voice_start', 'user_question');

        await interruptAIResponse();

        hasSavedRef.current = false;
        console.log('🔄 hasSavedRef 리셋됨:', hasSavedRef.current);

        setIsRecording(true);
        setError(null);
        setPendingQuestion('');
        setIsAnswerComplete(false);

        setIsSummarizingSection(false);
        setSummarizingSectionIndex(null);

        const tempMessageId = `pending_${Date.now()}`;
        setPendingMessageId(tempMessageId);

        await aiServiceRef.current.startRecording();

      } else {
        // 🔥 로그 기록: 음성 녹음 종료
        await logAction('voice_stop', 'user_question');

        setIsRecording(false);
        setIsWaiting(true);
        setHasUserAskedQuestion(true);
        hasUserAskedQuestionRef.current = true;

        console.log('🎤 녹음 중지, 저장 준비 상태:', {
          hasSavedRef: hasSavedRef.current,
          hasUserAsked: hasUserAskedQuestionRef.current
        });

        await aiServiceRef.current.stopRecording();
      }

    } catch (error) {
      console.error('녹음 토글 실패:', error);
      setError('녹음 처리에 실패했습니다.');
      setIsRecording(false);
      setIsWaiting(false);
      setPendingMessageId(null);
      setHasUserAskedQuestion(false);
      setIsSummarizingSection(false);
      setSummarizingSectionIndex(null);

      hasSavedRef.current = false;
    }
  }, [isRecording, interruptAIResponse, logAction]);

  // 사용자 제스처를 받아 AI 음성 초기화 시작 (Chrome AudioContext 정책 충족)
  const startVoiceChat = useCallback(async () => {
    if (voiceConsented) return;
    setVoiceConsented(true);

    const args = pendingInitArgsRef.current;
    if (!args) {
      console.warn('⏳ 데이터 로드 전 음성 시작 - 로드 후 자동 시작 예약됨');
      return;
    }
    try {
      console.log('🚀 사용자 제스처 후 AI 초기화 시작');
      await initializeAI(args.sData, args.pData, args.dData, args.sessionData);
      console.log('✅ AI 초기화 완료');
    } catch (e) {
      console.error('❌ AI 초기화 실패:', e);
    }
  }, [voiceConsented]);

  // 데이터가 voiceConsented 이후에 로드된 경우 자동으로 init
  useEffect(() => {
    if (voiceConsented && pendingInitArgsRef.current && !initializingRef.current && !aiServiceRef.current) {
      const args = pendingInitArgsRef.current;
      initializeAI(args.sData, args.pData, args.dData, args.sessionData).catch(e =>
        console.error('❌ 지연 AI 초기화 실패:', e)
      );
    }
  }, [voiceConsented, surgery, patient]);

  // 🔥 수정된 음성창 토글 - 로그 기록 추가
  const handleVoiceAreaToggle = useCallback(async (isOpen: boolean) => {
    setIsVoiceAreaOpen(isOpen);
    
    // 🔥 로그 기록: 음성창 토글
    await logAction('click', isOpen ? 'voice_area_open' : 'voice_area_close', {
      qaCount: qaSets.length
    });
  }, [qaSets.length, logAction]);

  const cleanup = useCallback(() => {
    if (initializingRef.current) {
      return;
    }

    initializingRef.current = true;

    try {
      stopTTS();

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
        connectionCheckIntervalRef.current = null;
      }

      setIsRecording(false);
      setIsWaiting(false);
      setPendingMessageId(null);
      setCurrentQASet(null);
      setHasUserAskedQuestion(false);
      setPendingQuestion('');
      setIsAnswerComplete(false);

      setIsSummarizingSection(false);
      setSummarizingSectionIndex(null);
      setIsSectionSummaryComplete(false);

      hasUserAskedQuestionRef.current = false;
      pendingQuestionRef.current = '';

      isSummarizingSectionRef.current = false;

      if (aiServiceRef.current) {
        try {
          aiServiceRef.current.disconnect();
        } catch (error) {
          console.error('AI 서비스 정리 오류:', error);
        }
        aiServiceRef.current = null;
      }

    } catch (error) {
      console.error('cleanup 오류:', error);
    } finally {
      initializingRef.current = false;
    }
  }, [stopTTS]);

  // initializeAI 함수 - 특이사항 포함
  const initializeAI = useCallback(async (sData: Surgery, pData: Patient, dData?: Doctor, sessionData?: PatientSession) => {
    if (initializingRef.current) {
      return;
    }

    if (connectionStatus === 'connecting' || connectionStatus === 'connected') {
      return;
    }

    initializingRef.current = true;

    try {
      setConnectionStatus('connecting');
      setError(null);
      setIsInitialAIResponseComplete(false);

      hasSavedRef.current = false;
      console.log('🔄 AI 초기화로 hasSavedRef 리셋됨');

      if (aiServiceRef.current) {
        try {
          aiServiceRef.current.disconnect();
        } catch {}
        aiServiceRef.current = null;
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      aiServiceRef.current = new OpenAIRealtimeService();

      // 콜백 설정
      aiServiceRef.current.onMessage((message: string, isUser: boolean, isComplete?: boolean) => {
        if (!isUser) {
          const currentIsSummarizing = isSummarizingSectionRef.current;

          if (!currentIsSummarizing) {
            setAiScript(message);
          }

          if (isComplete) {
            console.log('🎯 AI 응답 완료 감지');

            setIsRealtimeSpeaking(false);
            setIsWaiting(false);
            setIsAnswerComplete(true);

            if (currentIsSummarizing) {
              console.log('✅ 섹션 요약 완료 처리');
              setIsSummarizingSection(false);
              setSummarizingSectionIndex(null);
              setIsSectionSummaryComplete(true);
              isSummarizingSectionRef.current = false;

              setTimeout(() => {
                setIsSectionSummaryComplete(false);
              }, 3000);
            }

            const currentHasUserAsked = hasUserAskedQuestionRef.current;
            const currentPendingQuestion = pendingQuestionRef.current;
            const currentSurgery = surgeryRef.current;
            const currentPatient = patientRef.current;
            const currentDoctor = doctorRef.current;

            const shouldSave = !hasSavedRef.current
                && currentHasUserAsked
                && currentPendingQuestion.trim()
                && message.trim()
                && currentSurgery
                && currentPatient
                && !currentIsSummarizing;

            console.log('🤔 Firebase 저장 조건 체크:', {
              hasSavedRef: hasSavedRef.current,
              currentHasUserAsked,
              hasPendingQuestion: !!currentPendingQuestion.trim(),
              hasMessage: !!message.trim(),
              hasSurgery: !!currentSurgery,
              hasPatient: !!currentPatient,
              isNotSummarizing: !currentIsSummarizing,
              shouldSave
            });

            if (shouldSave) {
              console.log('🚀 Firebase 저장 조건 충족, 저장 시작');

              hasSavedRef.current = true;

              const questionToSave = currentPendingQuestion.trim();
              const answerToSave = message.trim();

              console.log('💾 Firebase 저장 실행:', {
                question: `"${questionToSave.substring(0, 50)}..."`,
                answer: `"${answerToSave.substring(0, 50)}..."`,
                surgery: currentSurgery.name,
                patient: currentPatient.name,
                doctor: currentDoctor?.name || 'general'
              });

              setTimeout(() => {
                saveQASetToFirebaseWithData(
                  questionToSave,
                  answerToSave,
                  currentSurgery,
                  currentPatient,
                  currentDoctor || undefined
                );
              }, 500);
            } else {
              console.log('⏭️ Firebase 저장 조건 미충족, 저장 건너뜀');
            }

            if (!isInitialAIResponseComplete) {
              setIsInitialAIResponseComplete(true);
            }
          } else {
            setIsRealtimeSpeaking(true);
            setIsAnswerComplete(false);
            setIsSectionSummaryComplete(false);
          }
        }
      });

      aiServiceRef.current.onTranscript((transcript, messageId, status) => {
        if (status === 'pending') {
          setAiScript(t('assistant.recognizing_voice'));
        } else if (status === 'completed') {
          if (transcript.trim()) {
            setPendingQuestion(transcript.trim());
            pendingQuestionRef.current = transcript.trim();

            setPendingMessageId(null);
            setIsWaiting(true);
          }
        }
      });

      // 특이사항 포함하여 연결
      // 🔥 AI 대화는 항상 한국어로 (UI 언어와 무관하게)
      await aiServiceRef.current.connect(
        sData,
        pData,
        'ko',
        dData,
        sessionData?.specialConditions,
        stage
      );

      if (!aiServiceRef.current.isConnected()) {
        throw new Error('연결이 완전히 설정되지 않았습니다.');
      }

      setConnectionStatus('connected');
      setIsConnected(true);
      setAiScript('연결이 완료되었습니다! AI 응답을 기다리는 중...');

    } catch (err) {
      console.error('AI 초기화 오류:', err);
      setConnectionStatus('error');
      setError('음성 기능을 사용할 수 없습니다.');

    } finally {
      initializingRef.current = false;
    }
  }, [connectionStatus, saveQASetToFirebaseWithData, isInitialAIResponseComplete, isSummarizingSection]);

  const handleForceReconnect = useCallback(async () => {
    if (!surgery || !patient || initializingRef.current) return;

    try {
      setError('재연결 중...');
      setHasAutoReconnectAttempted(false);

      if (aiServiceRef.current) {
        await aiServiceRef.current.forceReconnect();
      } else {
        await initializeAI(surgery, patient, doctor || undefined, patientSession || undefined);
      }
      setError(null);
    } catch (err) {
      console.error('재연결 실패:', err);
      setError('재연결에 실패했습니다. 음성 기능을 사용할 수 없습니다.');
    }
  }, [surgery, patient, doctor, patientSession, initializeAI]);

  // 상태 메시지 - 섹션 요약 포함
  const getStatusMessage = () => {
    switch (connectionStatus) {
      case 'connecting': return t('assistant.status.connecting');
      case 'connected':
        if (isSummarizingSection) return t('assistant.status.summarizing');
        if (isRecording) return t('assistant.status.recording');
        if (isWaiting) return t('assistant.status.waiting');
        if (isRealtimeSpeaking) return t('assistant.status.speaking');
        if (isSavingToFirebase) return t('assistant.status.saving');
        if (!isInitialAIResponseComplete) return t('assistant.status.initialWait');
        if (isSectionSummaryComplete) return t('assistant.status.summaryComplete');
        return t('assistant.status.ready');
      case 'error': return t('assistant.status.error');
      case 'disconnected': return t('assistant.status.disconnected');
      default: return t('assistant.status.checking');
    }
  };

  // 🔥 뒤로가기 버튼 핸들러
  const handleGoBack = useCallback(async () => {
    try {
      // 뒤로가기 액션 로깅
      await logAction('click', 'go_back', {
        currentPage: 'patient_assistant_page',
        qaCount: qaSets.length
      });

      // 세션 종료
      if (isInitialized) {
        await endSession();
      }

      // 이전 페이지로 이동
      navigate(-1);
    } catch (error) {
      console.error('뒤로가기 오류:', error);
      // 오류가 발생해도 이동은 실행
      navigate(-1);
    }
  }, [navigate, logAction, endSession, isInitialized, qaSets.length]);

  const handleConnectionLost = useCallback(async () => {
    if (!surgery || !patient || initializingRef.current) return;

    try {
      setError('연결이 끊어졌습니다. 재연결을 시도합니다...');

      if (aiServiceRef.current) {
        await aiServiceRef.current.forceReconnect();
      } else {
        await initializeAI(surgery, patient, doctor || undefined, patientSession || undefined);
      }
      setError(null);
    } catch (err) {
      console.error('강제 재연결 실패:', err);
      setError('재연결에 실패했습니다. 음성 기능을 사용할 수 없습니다.');
    }
  }, [surgery, patient, doctor, patientSession, initializeAI]);

  const loadData = async () => {
    try {
      console.log('🔄 loadData 시작');
      setLoading(true);
      setError(null);
      setConnectionStatus('disconnected');

      // 🔥 URL 파라미터 디코딩
      const decodedSurgeryName = surgeryName ? decodeURIComponent(surgeryName) : '';
      const decodedPatientName = urlPatientName ? decodeURIComponent(urlPatientName) : '';
      const decodedDoctorId = doctorId ? decodeURIComponent(doctorId) : undefined;

      console.log('📋 디코딩된 파라미터:', {
        원본: { surgeryName, urlPatientName, doctorId },
        디코딩: { decodedSurgeryName, decodedPatientName, decodedDoctorId }
      });

      if (!decodedSurgeryName || !decodedPatientName) {
        console.error('❌ 필수 파라미터 누락:', { decodedSurgeryName, decodedPatientName });
        setError('필요한 정보가 누락되었습니다.');
        return;
      }

      // 기관번호 확인
      let detectedInstitutionNumber = sessionStorage.getItem('institutionNumber');
      if (!detectedInstitutionNumber) {
        detectedInstitutionNumber = 'UNKNOWN';
      }
      setInstitutionNumber(detectedInstitutionNumber);
      console.log('🏥 기관번호 확인:', detectedInstitutionNumber);

      // 데이터 로드 프로미스 생성
      const loadPromises: Promise<any>[] = [
        getSurgeryByName(decodedSurgeryName),
        getPatientByName(detectedInstitutionNumber, decodedPatientName)
      ];

      if (decodedDoctorId) {
        loadPromises.push(getDoctorById(decodedDoctorId));
      }

      console.log('📡 Firebase 데이터 요청 시작');
      const results = await Promise.all(loadPromises) as [Surgery, Patient, Doctor?];
      const [sData, pData, dData] = results as [Surgery, Patient, Doctor | undefined];

      // 데이터 유효성 검사
      if (!sData) {
        console.error('❌ 수술 정보 없음');
        return setError('수술 정보를 찾을 수 없습니다.');
      }
      if (!pData) {
        console.error('❌ 환자 정보 없음');
        return setError('환자 정보를 찾을 수 없습니다.');
      }
      if (decodedDoctorId && !dData) {
        console.error('❌ 의사 정보 없음');
        return setError('담당 의사 정보를 찾을 수 없습니다.');
      }

      console.log('✅ 기본 데이터 로드 성공:', {
        surgery: sData.name,
        patient: pData.name,
        doctor: dData?.name || '일반'
      });

      // 상태 업데이트
      setSurgery(sData);
      setPatient(pData);
      setDoctor(dData || null);

      surgeryRef.current = sData;
      patientRef.current = pData;
      doctorRef.current = dData || null;

      // 환자 세션 정보 로드 (맞춤형 섹션 포함)
      try {
        console.log('🔍 환자 세션 정보 로드 시작');
        const sessions = await getPatientSessions(detectedInstitutionNumber, pData.name);
        const currentSession = sessions.find(session =>
          session.surgeryName === sData.name &&
          session.doctorId === (dData?.id || 'general')
        );

        if (currentSession) {
          setPatientSession(currentSession);
          console.log('✅ 환자 세션 로드됨:', {
            sessionId: currentSession.id,
            hasSpecialConditions: !!currentSession.specialConditions,
            hasCustomizedSections: !!(currentSession.customizedSections?.length)
          });

          // 맞춤형 섹션 사용 (세션에 있으면 사용, 없으면 원본 사용)
          if (currentSession.customizedSections && currentSession.customizedSections.length > 0) {
            setSections(currentSession.customizedSections);
            console.log('✅ 맞춤형 섹션 사용:', currentSession.customizedSections.length, '개');
          } else {
            setSections(sData.sections);
            console.log('⚠️ 원본 섹션 사용 (맞춤형 섹션 없음):', sData.sections.length, '개');
          }
        } else {
          console.log('⚠️ 해당 세션을 찾을 수 없음, 원본 섹션 사용');
          setSections(sData.sections);
        }
      } catch (sessionError) {
        console.error('❌ 세션 로드 오류:', sessionError);
        setSections(sData.sections);
      }

      // 기존 질문 기록 로드
      try {
        console.log('🔍 기존 QA 기록 로드 시작');
        const existing = await getQuestionsByPatientAndDoctor(
          sData.name,
          detectedInstitutionNumber,
          pData.name,
          dData?.id
        );
        
        if (existing) {
          setQuestionRecord(existing);
          const convertedQASets: QASet[] = existing.questions.map((question, index) => ({
            question,
            answer: existing.answers[index] || '',
            timestamp: existing.createdAt
          }));
          setQASets(convertedQASets);
          console.log('✅ 기존 QA 세트 로드됨:', convertedQASets.length, '개');
        } else {
          setQASets([]);
          setQuestionRecord(null);
          console.log('📝 새로운 환자-수술 조합, 빈 QA 세트로 시작');
        }
      } catch (error) {
        console.error('❌ 질문 기록 로드 실패:', error);
        setQASets([]);
        setQuestionRecord(null);
      }

      // AI 초기화 인자만 보관. 사용자가 음성 시작 버튼을 누르면 초기화 시작.
      // (Chrome 자동재생 정책: AudioContext는 사용자 제스처 후에만 동작)
      console.log('🤖 AI 초기화 대기 중 (사용자 제스처 필요)');
      try {
        const sessions = await getPatientSessions(detectedInstitutionNumber!, pData.name);
        const currentSession = sessions.find(session =>
          session.surgeryName === sData.name &&
          session.doctorId === (dData?.id || 'general')
        );
        pendingInitArgsRef.current = { sData, pData, dData, sessionData: currentSession || undefined };
      } catch (sessionLoadErr) {
        console.error('❌ 세션 로드 오류:', sessionLoadErr);
        pendingInitArgsRef.current = { sData, pData, dData };
      }

    } catch (err) {
      console.error('❌ loadData 전체 오류:', err);
      setError(`데이터를 불러오는 중 오류가 발생했습니다: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // useEffects

  useEffect(() => {
    console.log('🔍 PatientAssistantPage 초기화 시작');
    console.log('📋 URL 파라미터:', { surgeryName, patientName: urlPatientName, doctorId });
    console.log('🏥 기관번호:', institutionNumber);
    console.log('🔑 Realtime 세션 엔드포인트:', process.env.REACT_APP_REALTIME_SESSION_ENDPOINT || '/api/realtime-session');

    if (surgeryName && urlPatientName && !initializingRef.current && !hasLoadedRef.current) {
      console.log('✅ 조건 충족, 데이터 로딩 시작');
      hasLoadedRef.current = true;
      loadData().catch(error => {
        console.error('❌ loadData 오류:', error);
      });
    } else {
      console.log('❌ 조건 미충족:', {
        surgeryName: !!surgeryName,
        urlPatientName: !!urlPatientName,
        initializingRef: initializingRef.current,
        hasLoadedRef: hasLoadedRef.current
      });
    }

    return () => {
      console.log('🧹 PatientAssistantPage 클린업');
      // cleanup 호출은 의존성 배열에 cleanup이 있을 때만
    };
  }, [surgeryName, urlPatientName, doctorId]);

  // 🔥 데이터 로드 완료 후 세션 초기화
  useEffect(() => {
    if (surgery && patient && !isInitialized) {
      initializeSession();
    }
  }, [surgery, patient, initializeSession, isInitialized]);

  // 🔥 컴포넌트 언마운트 시 세션 종료
  useEffect(() => {
    return () => {
      if (isInitialized) {
        endSession();
      }
      cleanup();
    };
  }, [endSession, isInitialized, cleanup]);

  useEffect(() => {
    if (scriptRef.current) scriptRef.current.scrollTop = scriptRef.current.scrollHeight;
  }, [aiScript]);

  // ref 동기화 - 클로저 문제 해결
  useEffect(() => {
    hasUserAskedQuestionRef.current = hasUserAskedQuestion;
  }, [hasUserAskedQuestion]);

  useEffect(() => {
    pendingQuestionRef.current = pendingQuestion;
  }, [pendingQuestion]);

  useEffect(() => {
    surgeryRef.current = surgery;
  }, [surgery]);

  useEffect(() => {
    patientRef.current = patient;
  }, [patient]);

  useEffect(() => {
    doctorRef.current = doctor;
  }, [doctor]);

  useEffect(() => {
    isSummarizingSectionRef.current = isSummarizingSection;
  }, [isSummarizingSection]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (aiServiceRef.current && connectionStatus === 'connected') {
      intervalId = setInterval(() => {
        if (aiServiceRef.current && !initializingRef.current) {
          const isConnected = aiServiceRef.current.isConnected();
          if (!isConnected) {
            setConnectionStatus('disconnected');
            handleConnectionLost();
          }
        }
      }, 5000);

      connectionCheckIntervalRef.current = intervalId;
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [connectionStatus, handleConnectionLost]);

    if (loading) {
      return (
        <MobileContainer>
          <Header>
            <div>{t('assistant.loading')}</div>
          </Header>
        </MobileContainer>
      );
    }
  
    if (error && !surgery) {
      return (
        <MobileContainer>
          <ErrorMessage>
            <div>{t('common.error')}</div>
            <div>{t(error, { error: error })}</div>
            <RetryButton onClick={loadData} disabled={loading}>
              {loading ? t('assistant.retrying') : t('assistant.retry')}
            </RetryButton>
          </ErrorMessage>
        </MobileContainer>
      );
    }
  
    if (!surgery || !patient) {
      return (
        <MobileContainer>
          <ErrorMessage>{t('assistant.error_data_simple')}</ErrorMessage>
        </MobileContainer>
      );
    }

    const currentSection = sections[currentSectionIndex];

    // 섹션이 비어 있거나 인덱스 범위를 벗어나면 안전하게 폴백 표시
    if (!currentSection) {
      return (
        <MobileContainer>
          <ConnectionStatus status={connectionStatus} />
          <Header>
            {surgery.name}
            <GoBackButton onClick={handleGoBack} title={t('assistant.go_back_button_title')}>
              {t('assistant.back_button_label')}
            </GoBackButton>
          </Header>
          <ErrorMessage>
            {sections.length === 0
              ? '이 수술에 등록된 섹션이 없습니다. 관리자에게 문의해 주세요.'
              : '섹션을 불러올 수 없습니다.'}
          </ErrorMessage>
        </MobileContainer>
      );
    }
    const isButtonDisabled = connectionStatus !== 'connected' || !aiServiceRef.current?.isConnected() || isSavingToFirebase;
  
    return (
      <MobileContainer>
        <ConnectionStatus status={connectionStatus} />
        <Header>
          {surgery.name}
          <GoBackButton
            onClick={handleGoBack}
            title={t('assistant.go_back_button_title')}
            aria-label={t('assistant.go_back_button_aria')}
          >
            {t('assistant.close_button_label')}
          </GoBackButton>
          {doctor ? (
            <DoctorInfo>
              {t('assistant.header_doctor', { name: doctor.name })}
            </DoctorInfo>
          ) : (
            <DoctorInfo>
              {t('assistant.header_general')}
            </DoctorInfo>
          )}
          <PatientInfo>
            {t('assistant.header_patient', { name: patient.name })}
          </PatientInfo>
        </Header>
  
        {!isVoiceAreaOpen && (
          <SectionTabs>
            {sections.map((sec, i) => (
              <SectionTab
                key={sec.id}
                isActive={i === currentSectionIndex}
                isSummarizing={isSummarizingSection && summarizingSectionIndex === i}
                onClick={() => handleSectionChange(i)}
                title={
                  isSummarizingSection && summarizingSectionIndex === i
                    ? t('assistant.tab_summarizing_tooltip')
                    : t('assistant.tab_summary_tooltip')
                }
              >
                {sec.title}
              </SectionTab>
            ))}
          </SectionTabs>
        )}
  
        {isVoiceAreaOpen ? (
          <VoiceLogArea sectionTabsHidden={true}>
            <VoiceLogHeader>
              <VoiceLogTitle>{t('assistant.qa_log_title')}</VoiceLogTitle>
              <CloseVoiceButton onClick={() => handleVoiceAreaToggle(false)}>
                {t('assistant.qa_log_close')}
              </CloseVoiceButton>
            </VoiceLogHeader>
  
            <QALogContainer>
              {qaSets.length === 0 ? (
                <EmptyQALog>
                  <div>{t('assistant.qa_log_empty_title')}</div>
                  <div>{t('assistant.qa_log_empty_desc')}</div>
                </EmptyQALog>
              ) : (
                qaSets.map((qaSet, index) => (
                  <QALogItem key={index}>
                    <QuestionBubble>
                      <QALabel>{t('assistant.qa_log_question_label', { index: index + 1 })}</QALabel>
                      <QAText>{qaSet.question}</QAText>
                    </QuestionBubble>
                    <AnswerBubble>
                      <QALabel>{t('assistant.qa_log_answer_label')}</QALabel>
                      <QAText>{qaSet.answer}</QAText>
                    </AnswerBubble>
                  </QALogItem>
                ))
              )}
  
              {(pendingQuestion || (aiScript && hasUserAskedQuestion)) && (
                <QALogItem>
                  {pendingQuestion && (
                    <QuestionBubble>
                      <QALabel>{t('assistant.qa_log_question_label', { index: qaSets.length + 1 })}</QALabel>
                      <QAText>{pendingQuestion}</QAText>
                    </QuestionBubble>
                  )}
                  {aiScript && hasUserAskedQuestion && aiScript !== t('assistant.status.initialWait') && (
                    <AnswerBubble className={isRealtimeSpeaking ? 'speaking' : ''}>
                      <QALabel>{isRealtimeSpeaking ? t('assistant.qa_log_answer_in_progress_label') : t('assistant.qa_log_answer_label')}</QALabel>
                      <QAText>{aiScript}</QAText>
                    </AnswerBubble>
                  )}
                </QALogItem>
              )}
            </QALogContainer>
          </VoiceLogArea>
        ) : (
          <>
            <ContentArea ref={contentAreaRef}>
              <MarkdownContainer>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer">
                        {props.children}
                      </a>
                    ),
                  }}
                >
                  {currentSection.content}
                </ReactMarkdown>
              </MarkdownContainer>
  
              {currentSection.attachments && currentSection.attachments.length > 0 && (
                <AttachmentSection>
                  {currentSection.attachments?.map(att => (
                    <AttachmentItem key={att.id}>
                      {att.type === 'image' ? (
                        <Image 
                          src={att.url} 
                          alt={att.name}
                          onClick={async () => {
                            await logAction('click', 'attachment_image', {
                              attachmentId: att.id,
                              attachmentName: att.name,
                              sectionIndex: currentSectionIndex
                            });
                          }}
                        />
                      ) : (
                        <Video 
                          controls
                          onClick={async () => {
                            await logAction('click', 'attachment_video', {
                              attachmentId: att.id,
                              attachmentName: att.name,
                              sectionIndex: currentSectionIndex
                            });
                          }}
                        >
                          <source src={att.url} type="video/mp4" />
                          Your browser does not support the video tag.
                        </Video>
                      )}
                    </AttachmentItem>
                  ))}
                </AttachmentSection>
              )}
            </ContentArea>
  
            <ExpandVoiceButton onClick={() => handleVoiceAreaToggle(true)}>
              {t('assistant.expand_button', { count: qaSets.length })}
            </ExpandVoiceButton>
          </>
        )}
  
        <AIAssistant isVoiceAreaOpen={isVoiceAreaOpen}>
          <ControlsArea>
            <StatusInfo>
              <StatusIcon>
                <StethoscopeIcon size={20} />
              </StatusIcon>
              <StatusText>
                <StatusTitle>{t('assistant.ai_assistant_title')}</StatusTitle>
                <StatusDescription>{getStatusMessage()}</StatusDescription>
              </StatusText>
            </StatusInfo>

            <ControlButtons>
              <ControlButton
                variant="mic"
                isActive={isRecording}
                onClick={toggleRecording}
                disabled={isButtonDisabled}
                title={
                  isRecording ? t('assistant.mic_button_title_stop') :
                  (isRealtimeSpeaking || isWaiting || isSummarizingSection) ? t('assistant.mic_button_title_interrupt') :
                  t('assistant.mic_button_title_record')
                }
              >
                {isRecording ? <StopIcon size={18} /> : <MicIcon size={18} />}
                {isRecording && <RecordingIndicator />}
              </ControlButton>

              {(connectionStatus === 'error' || connectionStatus === 'disconnected') && (
                <ControlButton
                  variant="stop"
                  onClick={handleForceReconnect}
                  title={t('assistant.reconnect_button_title')}
                >
                  <ReconnectIcon size={18} />
                </ControlButton>
              )}
            </ControlButtons>
          </ControlsArea>
        </AIAssistant>

        {!voiceConsented && (
          <StartOverlay>
            <StartCard>
              <StartIconWrap>
                <MicIcon size={32} />
              </StartIconWrap>
              <StartTitle>{t('assistant.start_overlay.title')}</StartTitle>
              <StartDescription>{t('assistant.start_overlay.description')}</StartDescription>
              <StartButton onClick={startVoiceChat}>
                {t('assistant.start_overlay.button')}
              </StartButton>
              <StartHint>{t('assistant.start_overlay.hint')}</StartHint>
            </StartCard>
          </StartOverlay>
        )}
      </MobileContainer>
    );
  };
  
  export default PatientAssistantPage;

// 스타일 컴포넌트들
const Header = styled.div`
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  color: white;
  padding: 20px;
  height: 80px;
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
`;

const PatientInfo = styled.div`
  position: absolute;
  right: 8px;
  top: 58px;
  font-size: 10px;
  color: white;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DoctorInfo = styled.div`
  position: absolute;
  left: 8px;
  top: 58px;
  font-size: 10px;
  color: white;
  font-weight: 500;
`;

const ConnectionStatus = styled.div<{ status: 'connecting' | 'connected' | 'disconnected' | 'error' }>`
  position: fixed;
  top: 10px;
  left: 10px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  z-index: 200;
  ${props => {
    switch (props.status) {
      case 'connected': return 'background-color: #15803d;';
      case 'connecting': return 'background-color: #f59e0b; animation: pulse 1s infinite;';
      case 'disconnected':
      case 'error': return 'background-color: #ef4444;';
      default: return 'background-color: #6b7280;';
    }
  }}
  @keyframes pulse { 0%,100%{opacity:1;}50%{opacity:0.5;} }
`;

const ErrorMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #ef4444;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin: 20px;
`;

const RetryButton = styled.button`
  background-color: #0369a1;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 12px;

  &:hover {
    background-color: #075985;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SectionTabs = styled.div`
  display: flex;
  overflow-x: auto;
  padding: 8px 16px;
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  z-index: 90;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
`;

const SectionTab = styled.button<{ isActive: boolean; isSummarizing?: boolean }>`
  padding: 12px 16px;
  margin-right: 12px;
  border: none;
  border-radius: 20px;
  background-color: ${props =>
    props.isSummarizing ? '#15803d' :
    props.isActive ? '#0369a1' : '#f3f4f6'
  };
  color: ${props =>
    props.isSummarizing ? 'white' :
    props.isActive ? 'white' : '#374151'
  };
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  ${props => props.isSummarizing && `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      width: 8px;
      height: 8px;
      background-color: white;
      border-radius: 50%;
      animation: pulse 1s infinite;
    }
  `}

  &:hover {
    background-color: ${props =>
      props.isSummarizing ? '#166534' :
      props.isActive ? '#075985' : '#e5e7eb'
    };
  }
`;

const ContentArea = styled.div`
  position: fixed;
  top: 144px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  bottom: 80px;
  overflow-y: auto;
  padding: 20px;
  background-color: white;
`;

const MarkdownContainer = styled.div`
  line-height: 1.6;
  color: #374151;

  & ul {
    padding-left: 1.2em;
    margin-bottom: 1em;
  }

  & ol {
    padding-left: 1.2em;
    margin-bottom: 1em;
  }

  & h1, & h2, & h3, & h4, & h5 {
    margin-top: 1em;
    font-weight: 600;
    color: #1f2937;
  }

  & p {
    margin-bottom: 0.8em;
  }
`;

const AttachmentSection = styled.div`
  margin-top: 20px;
`;

const AttachmentItem = styled.div`
  margin-bottom: 16px;
`;

const Image = styled.img`
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  cursor: pointer;
`;

const Video = styled.video`
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const VoiceLogArea = styled.div<{ sectionTabsHidden?: boolean }>`
  position: fixed;
  top: ${props => props.sectionTabsHidden ? '80px' : '144px'};
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  bottom: 80px;
  background-color: white;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
`;

const VoiceLogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
`;

const VoiceLogTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseVoiceButton = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const QALogContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyQALog = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;

  & div:first-child {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  & div:last-child {
    font-size: 14px;
  }
`;

const QALogItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionBubble = styled.div`
  align-self: flex-end;
  background-color: #0369a1;
  color: white;
  padding: 12px;
  border-radius: 16px 16px 4px 16px;
  max-width: 80%;
`;

const AnswerBubble = styled.div`
  align-self: flex-start;
  background-color: #f3f4f6;
  color: #374151;
  padding: 12px;
  border-radius: 16px 16px 16px 4px;
  max-width: 80%;

  &.speaking {
    background-color: #ecfdf5;
    border: 1px solid #a7f3d0;
  }
`;

const QALabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  opacity: 0.7;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QAText = styled.div`
  font-size: 14px;
  line-height: 1.4;
`;

const ExpandVoiceButton = styled.button`
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #0369a1;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(3, 105, 161, 0.3);
  z-index: 85;

  &:hover {
    background-color: #075985;
    transform: translateX(-50%) translateY(-1px);
  }
`;

const AIAssistant = styled.div<{ isVoiceAreaOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  background: white;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 6px rgba(0,0,0,0.1);
`;

const ControlsArea = styled.div`
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const StatusIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  flex-shrink: 0;
`;

const StatusText = styled.div``;

const StatusTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
`;

const StatusDescription = styled.div`
  font-size: 12px;
  color: #15803d;
`;

const ControlButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ControlButton = styled.button<{ isActive?: boolean; variant?: 'mic' | 'send' | 'stop' }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  margin-left: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => {
    switch (props.variant) {
      case 'mic':
        return `background-color: ${props.isActive ? '#ef4444' : '#15803d'};
                &:hover { background-color: ${props.isActive ? '#dc2626' : '#166534'}; }`;
      case 'send':
        return `background-color: #0369a1;
                &:hover { background-color: #075985; }`;
      case 'stop':
        return `background-color: #f59e0b;
                &:hover { background-color: #d97706; }`;
      default:
        return `background-color: #0369a1;
                &:hover { background-color: #075985; }`;
    }
  }}

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RecordingIndicator = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 12px;
  height: 12px;
  background-color: #ef4444;
  border-radius: 50%;
  animation: pulse 1s infinite;
`;

const GoBackButton = styled.button`
  position: absolute;
  top: 20px;
  right: 10px;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const StartOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const StartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  width: 100%;
  max-width: 360px;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.25);
  text-align: center;
`;

const StartIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StartTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
`;

const StartDescription = styled.p`
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
  margin: 0 0 24px;
`;

const StartButton = styled.button`
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(135deg, #0369a1 0%, #075985 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(3, 105, 161, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StartHint = styled.div`
  margin-top: 16px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
`;
